<template>
  <transition :name="overlayTransition">
    <div
      v-show="visibility.overlay"
      ref="overlay"
      :class="overlayClass"
      :aria-expanded="visibility.overlay.toString()"
      :data-modal="name"
    >
      <div
        class="v--modal-background-click"
        @mousedown.self="handleBackgroundClick"
        @touchstart.self="handleBackgroundClick"
      >
        <div v-show="visibility.modal" ref="modal" :class="modalClass" :style="modalStyle">
          <div v-if="header" class="m-header- m-dark-b" ref="header">
            <span class="grow-auto d-flex">{{ title }}</span>
            <button type="button" class="close" aria-label="Close" @click="$modal.hide(name)">
              <span aria-hidden="true">&times;</span>
            </button>
            <div class="absolute-center">
              <slot name="header-center"></slot>
            </div>
          </div>
          <slot name="fixed-top"></slot>
          <div class="m-content- m-dark-b" ref="content" :class="{ 'm-content-padding': withPadding, top: pTop }">
            <div
              class="m-content-inner"
              :class="{ 'mb-4': withPadding, 'p-relative': contentRelative }"
              :style="contentStyle"
            >
              <slot></slot>
            </div>
          </div>
          <slot name="after-main"></slot>
          <div v-if="footer" class="m-footer- btn-con no-wrap m-dark-b" ref="footer">
            <slot name="footer-before"></slot>
            <span class="m-auto"></span>
            <div
              v-if="cancelText"
              @click="cancelOverride ? emitCancel() : $modal.hide(name)"
              class="btn btn-light text-gray"
            >
              {{ cancelText }}
            </div>
            <slot name="footer"></slot>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>
<script>
import _ from 'lodash';
import Modal from 'vue-js-modal';
import { QueryFunctions } from '@/services/helper.ts';

export const generateId = ((index = 0) => () => (index++).toString())();

export const createModalEvent = (args = {}) => {
  return {
    id: generateId(),
    timestamp: Date.now(),
    canceled: false,
    ...args,
  };
};
export const blurActiveElement = () => {
  if (
    typeof document !== 'undefined' &&
    document.activeElement &&
    document.activeElement.tagName !== 'BODY' &&
    document.activeElement.blur
  ) {
    document.activeElement.blur();

    return true;
  }

  return false;
};

export default {
  name: 'VueJsModalWrapper',
  props: {
    title: String,
    cancelText: String,
    name: {
      required: true,
      type: String,
    },
    delay: {
      type: Number,
      default: 0,
    },
    adaptive: {
      type: Boolean,
      default: false,
    },
    draggable: {
      type: [Boolean, String],
      default: false,
    },
    scrollable: {
      type: Boolean,
      default: false,
    },
    reset: {
      type: Boolean,
      default: false,
    },
    overlayTransition: {
      type: String,
      default: 'overlay-fade',
    },
    transition: {
      type: String,
    },
    clickToClose: {
      type: Boolean,
      default: false,
    },
    classes: {
      type: [String, Array],
      default: 'v--modal',
    },
    maxWidth: {
      type: Number,
      default: Infinity,
    },
    maxHeight: {
      type: Number,
      default: Infinity,
    },
    width: {
      type: [Number, String],
      default: '368',
    },
    height: {
      type: [String, Number],
      default: '400',
    },
    cancelOverride: {
      type: Boolean,
      default: false,
    },
    header: {
      type: Boolean,
      default: true,
    },
    footer: {
      type: Boolean,
      default: true,
    },
    withPadding: {
      type: Boolean,
      default: true,
    },
    contentRelative: {
      type: Boolean,
      default: false,
    },
    pTop: {
      type: Boolean,
      default: true,
    },
    toTop: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      visible: false,

      visibility: {
        modal: false,
        overlay: false,
      },

      ScrolledTop: false,
      ScrolledBottom: false,
      minSize: 75,
    };
  },
  destroyed() {
    this.$refs.content && this.$refs.content.removeEventListener('scroll', this.handleScroll);
  },
  created() {},

  mounted() {
    const content = this.$refs.content;
    if (content) content.addEventListener('scroll', this.handleScroll);
  },

  /**
   * Sets global listeners
   */
  beforeMount() {
    Modal.event.$on('toggle', this.handleToggleEvent);

    if (this.clickToClose) {
      window.addEventListener('keyup', this.handleEscapeKeyUp);
    }
  },
  /**
   * Removes global listeners
   */
  beforeDestroy() {
    Modal.event.$off('toggle', this.handleToggleEvent);

    if (this.clickToClose) {
      window.removeEventListener('keyup', this.handleEscapeKeyUp);
    }
    /**
     * Removes blocked scroll
     */
    if (this.scrollable) {
      document.body.classList.remove('v--modal-block-scroll');
    }
  },
  computed: {
    trueModalWidth() {
      return _.startsWith(String(this.width), 'full')
        ? this.maxWidth
          ? this.maxWidth + 'px'
          : '100%'
        : this.width + (Number(this.width) ? 'px' : '');
    },
    /**
     * Returns class list for screen overlay (modal background)
     */
    overlayClass() {
      return {
        'modal-wrapper': true,
        'padded-modal': this.width && _.endsWith(String(this.width), 'padded'),
        'v--modal-overlay': true,
        scrollable: this.scrollable,
      };
    },
    /**
     * Returns class list for modal itself
     */
    modalClass() {
      return ['v--modal-box', this.classes];
    },
    /**
     * CSS styles for position and size of the modal
     */
    modalStyle() {
      return {
        width: this.trueModalWidth,
        'margin-top': this.toTop ? '32px' : undefined,
      };
    },
    contentStyle() {
      const attr = {};
      if (this.height !== undefined || this.height !== null) {
        attr.height = Number(this.height) == this.height ? this.height + 'px' : this.height;
      }
      return attr;
    },
  },
  watch: {
    /**
     * Sets the visibility of overlay and modal.
     * Events 'opened' and 'closed' is called here
     * inside `setTimeout` and `$nextTick`, after the DOM changes.
     * This fixes `$refs.modal` `undefined` bug (fixes #15)
     */
    visible(value) {
      if (value) {
        this.visibility.overlay = true;
        setTimeout(() => {
          this.visibility.modal = true;
          this.$nextTick(() => {
            this.callAfterEvent(true);
          });
        }, this.delay);
      } else {
        this.visibility.modal = false;
        setTimeout(() => {
          this.visibility.overlay = false;
          this.$nextTick(() => {
            this.callAfterEvent(false);
          });
        }, this.delay);
      }
    },
  },

  methods: {
    handleToggleEvent(name, state, params) {
      if (this.name === name) {
        const nextState = typeof state === 'undefined' ? !this.visible : state;

        this.toggle(nextState, params);
      }
    },

    handleEscapeKeyUp(event) {
      if (event.which === 27 && this.visible) {
        this.$modal.hide(this.name);
      }
    },

    handleScroll() {
      const el = this.$refs.content;
      const top = el.scrollTop;
      const bottom = el.scrollHeight - el.offsetHeight - top;
      const isTop = top < this.minSize;
      const isBottom = bottom < this.minSize;

      if (isTop !== this.ScrolledTop) {
        this.ScrolledTop = isTop;
        this.$emit('scrollStart', isTop);
      }

      if (isBottom !== this.ScrolledBottom) {
        this.ScrolledBottom = isBottom;
        this.$emit('scrollEnd', isBottom);
      }
    },

    /**
     * Generates event object
     */
    createModalEvent(args = {}) {
      return createModalEvent({
        name: this.name,
        ref: this.$refs.modal,
        ...args,
      });
    },
    /**
     * Event handler which is triggered on $modal.show and $modal.hide
     * BeforeEvents: ('before-close' and 'before-open') are `$emit`ed here,
     * but AfterEvents ('opened' and 'closed') are moved to `watch.visible`.
     */
    toggle(nextState, params) {
      const { reset, scrollable, visible } = this;

      if (visible === nextState) {
        return;
      }
      this.visible = true;

      const beforeEventName = visible ? 'before-close' : 'before-open';

      if (beforeEventName === 'before-open') {
        /**
         * Need to unfocus previously focused element, otherwise
         * all keypress events (ESC press, for example) will trigger on that element.
         */
        blurActiveElement();

        if (scrollable) {
          document.body.classList.add('v--modal-block-scroll');
        }
      } else {
        if (scrollable) {
          document.body.classList.remove('v--modal-block-scroll');
        }
      }

      let stopEventExecution = false;

      const stop = () => {
        stopEventExecution = true;
      };

      const beforeEvent = this.createModalEvent({
        stop,
        state: nextState,
        params,
      });

      this.$emit(beforeEventName, beforeEvent);

      if (!stopEventExecution) {
        this.visible = nextState;
      }
      this.beforeEventCustom(beforeEventName, beforeEvent);
    },

    /**
     * Event handler that is triggered when background overlay is clicked
     */
    handleBackgroundClick() {
      if (this.clickToClose) {
        this.toggle(false);
      }
    },

    /**
     *'opened' and 'closed' events are `$emit`ed here.
     * This is called in watch.visible.
     * Because modal DOM updates are async,
     * wrapping afterEvents in `$nextTick` fixes `$refs.modal` undefined bug.
     * (fixes #15)
     */
    callAfterEvent(state) {
      const eventName = state ? 'opened' : 'closed';
      const event = this.createModalEvent({ state });
      this.$emit(eventName, event);
    },

    afterTransitionEnter() {
      // console.log('after transition enter')
    },

    afterTransitionLeave() {
      // console.log('after transition leave')
    },

    beforeEventCustom(eventName, event) {
      switch (eventName) {
        case 'before-open':
          // add to query
          QueryFunctions.setKeyToQuery(this.name);
          this.$emit('open', event);
          return;
        case 'before-close':
          // remove from query
          QueryFunctions.goBack(this.name);
          return;
      }
    },

    emitCancel() {
      this.$emit('on-cancel');
    },
  },
};
</script>

<style></style>

<style scoped>
.m-header-,
.m-footer- {
  white-space: nowrap;
  display: flex;
  align-items: center;
  padding: 14px 24px;
  line-height: 18px;
  background-color: #f1f4f8;
  outline: none;
}
.-header- {
  position: relative;
}
.m-footer- {
  padding-top: 16px;
  padding-bottom: 16px;
}
.m-footer-after {
  margin: 8px 4px;
  padding-right: 4px;
  padding-left: 4px;
  overflow-y: auto;
  min-height: 54px !important;
}
.m-content-padding {
  padding: 0 24px;
}
.m-content-padding.top {
  padding-top: 18px;
}
.m-content- {
  overflow-y: auto;
}
.m-dark-b:not(:last-child) {
  border-bottom: solid 1px #c7d2e0;
}
.m-content-inner {
  width: 100%;
}
.rtl .m-content-inner {
  text-align: right;
}
.modal-wrapper div:first-child {
  border-top-right-radius: 4px;
  border-top-left-radius: 4px;
}
.modal-wrapper div:last-child {
  border-bottom-right-radius: 4px;
  border-bottom-left-radius: 4px;
}
.absolute-center {
  position: absolute;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
}
.z-top {
  z-index: 10;
}
</style>
